import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save, RefreshCw, Building2, DollarSign, Briefcase, Plus, Trash2, Camera, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCompany, useCreateCompany, useUpdateCompany } from '@/hooks/useCompanies';
import { useAuth } from '@/contexts/AuthContext';
import { useSalesReps } from '@/hooks/useUsers';
import { useAppConfig } from '@/hooks/useAppConfig';
import { createContact } from '@/services/companies';
import { parseBusinessCard, type BusinessCardData } from '@/services/businessCardRecognition';
import type { CreateCompanyInput, BusinessVisibility, CommissionTier, CreateContactInput } from '@/types';

// Default company types (fallback if not configured in admin settings)
const DEFAULT_COMPANY_TYPES = [
  'Insurance Agent',
  'Plumber',
  'Restoration Company',
  'Property Manager',
  'Real Estate Agent',
  'Contractor',
  'Adjuster',
  'Attorney',
  'Other',
];

export function CompanyFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { user } = useAuth();
  const { data: company, isLoading } = useCompany(id);
  const { data: salesReps } = useSalesReps();
  const { data: appConfig } = useAppConfig();
  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany();

  // Get company types from config or use defaults
  const companyTypes = appConfig?.companyTypes || DEFAULT_COMPANY_TYPES;

  // Notification user IDs (legacy - kept for data compatibility)
  const [selectedNotifyUserIds, setSelectedNotifyUserIds] = useState<number[]>([]);

  // Business visibility checkboxes
  const [visibleToACR, setVisibleToACR] = useState(true);
  const [visibleToBlacksmith, setVisibleToBlacksmith] = useState(true);

  // Skip visit tracking (for internet/non-physical companies)
  const [skipVisitTracking, setSkipVisitTracking] = useState(false);

  // Business card scanning
  const [isScanning, setIsScanning] = useState(false);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const scannedContactRef = useRef<Omit<CreateContactInput, 'isPrimary'> | null>(null);

  // Commission tiers for tiered commission type
  const [commissionTiers, setCommissionTiers] = useState<CommissionTier[]>([
    { minRevenue: 0, maxRevenue: 5000, percentage: 0.01 },
    { minRevenue: 5000, maxRevenue: 10000, percentage: 0.02 },
    { minRevenue: 10000, maxRevenue: undefined, percentage: 0.03 },
  ]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<CreateCompanyInput>();

  const commissionType = watch('commissionType');

  // Handle business card scan
  const handleScanBusinessCard = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be re-selected
    e.target.value = '';

    setIsScanning(true);
    try {
      const result = await parseBusinessCard(file, companyTypes);

      if (!result.success) {
        toast.error(result.warnings?.[0] || 'Could not read business card. Try a clearer image.');
        return;
      }

      const d = result.data;

      // Populate form fields
      setValue('name', d.companyName, { shouldDirty: true });
      if (d.companyType) setValue('type', d.companyType, { shouldDirty: true });
      if (d.address) setValue('address', d.address, { shouldDirty: true });
      if (d.city) setValue('city', d.city, { shouldDirty: true });
      if (d.state) setValue('state', d.state, { shouldDirty: true });
      if (d.zip) setValue('zip', d.zip, { shouldDirty: true });
      if (d.phone) setValue('phone', d.phone, { shouldDirty: true });
      if (d.email) setValue('email', d.email, { shouldDirty: true });
      if (d.website) setValue('website', d.website, { shouldDirty: true });

      // Store contact data for auto-creation after company save
      if (d.contactFirstName || d.contactLastName) {
        scannedContactRef.current = {
          firstName: d.contactFirstName || '',
          lastName: d.contactLastName || '',
          title: d.contactTitle || undefined,
          email: d.contactEmail || d.email || undefined,
          phone: d.contactPhone || d.phone || undefined,
        };
      }

      // Show warnings if any
      if (result.warnings && result.warnings.length > 0) {
        toast(result.warnings.join('. '), { icon: '⚠️', duration: 5000 });
      }

      const confidenceLabel = result.confidence === 'high' ? 'High' : result.confidence === 'medium' ? 'Medium' : 'Low';
      toast.success(`Business card scanned (${confidenceLabel} confidence) - review fields and save`);
    } catch (err: any) {
      console.error('[CompanyForm] Business card scan error:', err);
      toast.error(err.message || 'Failed to scan business card. Please try again.');
    } finally {
      setIsScanning(false);
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        type: company.type || '',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        zip: company.zip || '',
        phone: company.phone || '',
        email: company.email || '',
        website: company.website || '',
        notes: company.notes || '',
        salesRepUserId: company.salesRepUserId || '',
        commissionType: company.commissionType || undefined,
        commissionValue: company.commissionType === 'percentage'
          ? (company.commissionValue || 0) * 100  // Convert from decimal to percentage
          : company.commissionValue || undefined,
        commissionCap: company.commissionCap || undefined,
        communicationType: company.communicationType || undefined,
        reminderFrequency: company.reminderFrequency || undefined,
      });
      // Set notification user IDs from company
      setSelectedNotifyUserIds(company.noteNotifyUserIds || []);
      // Set business visibility from company
      const visibility = company.businessVisibility || 'both';
      setVisibleToACR(visibility === 'both' || visibility === 'azdisaster');
      setVisibleToBlacksmith(visibility === 'both' || visibility === 'blacksmith');
      // Set commission tiers if present
      if (company.commissionTiers && company.commissionTiers.length > 0) {
        setCommissionTiers(company.commissionTiers);
      }
      // Set skip visit tracking
      setSkipVisitTracking(company.skipVisitTracking || false);
    }
  }, [company, reset]);

  // Set default sales rep when creating new company — default to logged-in user
  useEffect(() => {
    if (!isEditing && user && salesReps && salesReps.length > 0) {
      // Find logged-in user in the salesReps list (must match by ID)
      const currentUserAsRep = salesReps.find((rep) => rep.id === user.id);

      // Default to logged-in user if they're a sales rep, otherwise fall back to owner
      const defaultRep = currentUserAsRep
        || salesReps.find((rep) => rep.role === 'owner');

      if (defaultRep) {
        setValue('salesRepUserId', defaultRep.id, { shouldValidate: true, shouldDirty: false });
      }
    }
  }, [isEditing, user, salesReps, setValue]);

  const onSubmit = async (data: CreateCompanyInput) => {
    try {
      // Populate sales rep name from selected user
      const inputData = { ...data };

      console.log('[CompanyForm] Form data received:', data);
      console.log('[CompanyForm] salesRepUserId from form:', data.salesRepUserId);
      console.log('[CompanyForm] Available salesReps:', salesReps);

      if (inputData.salesRepUserId && salesReps) {
        const selectedRep = salesReps.find((rep) => rep.id === inputData.salesRepUserId);
        console.log('[CompanyForm] Found selected rep:', selectedRep);
        if (selectedRep) {
          inputData.salesRepName = selectedRep.displayName;
        }
      }

      // Handle commission fields - convert percentage to decimal
      if (inputData.commissionType === 'percentage' && inputData.commissionValue) {
        inputData.commissionValue = inputData.commissionValue / 100;  // Convert 5% to 0.05
      }

      // Handle tiered commission - add tiers
      if (inputData.commissionType === 'tiered') {
        inputData.commissionTiers = commissionTiers;
        inputData.commissionValue = undefined; // Not used for tiered
      } else {
        inputData.commissionTiers = undefined;
      }

      // Clear commission fields if no commission type selected
      if (!inputData.commissionType) {
        inputData.commissionValue = undefined;
        inputData.commissionCap = undefined;
        inputData.commissionTiers = undefined;
      }

      // Add notification user IDs
      inputData.noteNotifyUserIds = selectedNotifyUserIds.length > 0 ? selectedNotifyUserIds : undefined;

      // Determine business visibility from checkboxes
      let businessVisibility: BusinessVisibility = 'both';
      if (visibleToACR && visibleToBlacksmith) {
        businessVisibility = 'both';
      } else if (visibleToACR) {
        businessVisibility = 'azdisaster';
      } else if (visibleToBlacksmith) {
        businessVisibility = 'blacksmith';
      } else {
        // If neither selected, default to both
        businessVisibility = 'both';
      }
      inputData.businessVisibility = businessVisibility;

      // Add skip visit tracking
      inputData.skipVisitTracking = skipVisitTracking;

      console.log('[CompanyForm] Final inputData being saved:', inputData);

      if (isEditing && id) {
        await updateMutation.mutateAsync({ id, input: inputData });
        navigate(`/companies/${id}`);
      } else {
        const created = await createMutation.mutateAsync(inputData);
        console.log('[CompanyForm] Created company:', created);

        // Auto-create primary contact if scanned from business card
        if (scannedContactRef.current && user) {
          try {
            const contactInput: CreateContactInput = {
              ...scannedContactRef.current,
              isPrimary: true,
            };
            await createContact(created.id, contactInput, user.id);
            console.log('[CompanyForm] Auto-created contact from business card scan');
          } catch (contactErr) {
            console.error('[CompanyForm] Failed to auto-create contact:', contactErr);
            // Don't block navigation - company was created successfully
            toast.error('Company created but failed to add contact. Please add manually.');
          }
          scannedContactRef.current = null;
        }

        navigate(`/companies/${created.id}`);
      }
    } catch (error) {
      console.error('[CompanyForm] Error saving company:', error);
      // Error handled by mutation
    }
  };

  if (isEditing && isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="animate-spin text-blue-600" size={32} />
        </div>
      </div>
    );
  }

  if (isEditing && !company) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">Company not found</p>
          <Link to="/companies" className="btn btn-primary mt-4">
            Back to Companies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Back button */}
      <Link
        to={isEditing ? `/companies/${id}` : '/companies'}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft size={20} />
        {isEditing ? 'Back to Company' : 'Back to Companies'}
      </Link>

      {/* Form card */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-gray-900">
            {isEditing ? 'Edit Company' : 'Add New Company'}
          </h1>
          {!isEditing && (
            <>
              <input
                ref={scanInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleScanBusinessCard}
              />
              <button
                type="button"
                disabled={isScanning}
                onClick={() => scanInputRef.current?.click()}
                className="btn btn-secondary flex items-center gap-2"
              >
                {isScanning ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Camera size={18} />
                    Scan Business Card
                  </>
                )}
              </button>
            </>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Company Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              className={`input ${errors.name ? 'input-error' : ''}`}
              placeholder="Enter company name"
              {...register('name', { required: 'Company name is required' })}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Company Type */}
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
              Company Type
            </label>
            <select id="type" className="input" {...register('type')}>
              <option value="">Select a type...</option>
              {companyTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Business Visibility */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Briefcase size={18} className="text-purple-600" />
              <label className="block text-sm font-medium text-gray-700">
                Business Visibility
              </label>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Select which businesses this referral partner works with. They will only see projects and branding for selected businesses.
            </p>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibleToACR}
                  onChange={(e) => setVisibleToACR(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">ACR</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibleToBlacksmith}
                  onChange={(e) => setVisibleToBlacksmith(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">Blacksmith</span>
              </label>
            </div>
            {!visibleToACR && !visibleToBlacksmith && (
              <p className="mt-2 text-xs text-amber-600">
                At least one business must be selected. Both will be enabled if you save without selection.
              </p>
            )}
          </div>

          {/* Skip Visit Tracking */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={skipVisitTracking}
                onChange={(e) => setSkipVisitTracking(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">
                  Skip Visit Tracking
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  Enable for internet leads, call centers, or other non-physical referral sources that don't require in-person visits.
                </p>
              </div>
            </label>

            {/* Communication settings - shown when skip visit tracking is on */}
            {skipVisitTracking && (
              <div className="mt-4 ml-7 space-y-3 border-t pt-3">
                <p className="text-sm font-medium text-gray-700">Communication Settings</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Communication Type</label>
                    <select className="input text-sm" {...register('communicationType')}>
                      <option value="">None</option>
                      <option value="email">Email</option>
                      <option value="text">Text Message</option>
                      <option value="phone_call">Phone Call</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Reminder Frequency</label>
                    <select className="input text-sm" {...register('reminderFrequency')}>
                      <option value="">No reminders</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="custom">Custom Date</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Set how you communicate with this company and how often you want to be reminded.
                </p>
              </div>
            )}
          </div>

          {/* Assigned Sales Rep */}
          <div>
            <label htmlFor="salesRepUserId" className="block text-sm font-medium text-gray-700 mb-1">
              Assigned Sales Rep <span className="text-red-500">*</span>
            </label>
            <select
              id="salesRepUserId"
              className={`input ${errors.salesRepUserId ? 'input-error' : ''}`}
              {...register('salesRepUserId', { required: 'Sales rep is required' })}
            >
              <option value="">Select a sales rep...</option>
              {salesReps?.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.displayName} ({rep.role})
                </option>
              ))}
            </select>
            {errors.salesRepUserId && (
              <p className="mt-1 text-sm text-red-600">{errors.salesRepUserId.message}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Sales rep for this referral partner. Required for project intake linking.
            </p>
          </div>

          {/* Address */}
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              Street Address
            </label>
            <input
              id="address"
              type="text"
              className="input"
              placeholder="123 Main St"
              {...register('address')}
            />
          </div>

          {/* City, State, Zip */}
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-3">
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                id="city"
                type="text"
                className="input"
                placeholder="City"
                {...register('city')}
              />
            </div>
            <div className="col-span-1">
              <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                id="state"
                type="text"
                className="input"
                placeholder="AZ"
                maxLength={2}
                {...register('state')}
              />
            </div>
            <div className="col-span-2">
              <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
                ZIP
              </label>
              <input
                id="zip"
                type="text"
                className="input"
                placeholder="85001"
                {...register('zip')}
              />
            </div>
          </div>

          {/* Phone and Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                id="phone"
                type="tel"
                className="input"
                placeholder="(555) 123-4567"
                {...register('phone')}
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                className={`input ${errors.email ? 'input-error' : ''}`}
                placeholder="contact@company.com"
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
          </div>

          {/* Website */}
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              id="website"
              type="text"
              className="input"
              placeholder="www.company.com"
              {...register('website')}
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              id="notes"
              rows={3}
              className="input"
              placeholder="Any additional notes about this referral source..."
              {...register('notes')}
            />
          </div>

          {/* Partner Commission Settings */}
          <div className="border-t pt-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign size={20} className="text-green-600" />
              <h3 className="font-medium text-gray-900">Partner Commission Settings</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Configure commission rates for this referral partner. These rates are used to calculate
              partner kickbacks when they refer projects.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Commission Type */}
              <div>
                <label htmlFor="commissionType" className="block text-sm font-medium text-gray-700 mb-1">
                  Commission Type
                </label>
                <select
                  id="commissionType"
                  className="input"
                  {...register('commissionType')}
                >
                  <option value="">No commission</option>
                  <option value="percentage">Percentage of Revenue</option>
                  <option value="flat">Flat Amount</option>
                  <option value="tiered">Tiered (Progressive)</option>
                </select>
              </div>

              {/* Commission Value (for percentage and flat only) */}
              {(commissionType === 'percentage' || commissionType === 'flat') && (
                <div>
                  <label htmlFor="commissionValue" className="block text-sm font-medium text-gray-700 mb-1">
                    {commissionType === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                  </label>
                  <input
                    id="commissionValue"
                    type="number"
                    step={commissionType === 'percentage' ? '0.1' : '1'}
                    min="0"
                    max={commissionType === 'percentage' ? '100' : undefined}
                    className="input"
                    placeholder={commissionType === 'percentage' ? '5' : '500'}
                    {...register('commissionValue', {
                      min: { value: 0, message: 'Must be positive' },
                      max: commissionType === 'percentage'
                        ? { value: 100, message: 'Max 100%' }
                        : undefined,
                    })}
                  />
                  {errors.commissionValue && (
                    <p className="mt-1 text-sm text-red-600">{errors.commissionValue.message}</p>
                  )}
                </div>
              )}

              {/* Commission Cap (only for percentage) */}
              {commissionType === 'percentage' && (
                <div>
                  <label htmlFor="commissionCap" className="block text-sm font-medium text-gray-700 mb-1">
                    Cap ($)
                  </label>
                  <input
                    id="commissionCap"
                    type="number"
                    step="1"
                    min="0"
                    className="input"
                    placeholder="Optional max"
                    {...register('commissionCap', {
                      min: { value: 0, message: 'Must be positive' },
                    })}
                  />
                  {errors.commissionCap && (
                    <p className="mt-1 text-sm text-red-600">{errors.commissionCap.message}</p>
                  )}
                </div>
              )}
            </div>

            {commissionType && commissionType !== 'tiered' && (
              <p className="mt-2 text-xs text-gray-500">
                {commissionType === 'percentage'
                  ? 'Enter percentage (e.g., 5 for 5%). Optional cap limits max commission per project.'
                  : 'Enter flat dollar amount per project.'}
              </p>
            )}

            {/* Tiered Commission UI */}
            {commissionType === 'tiered' && (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-gray-600">
                  Define revenue brackets with different commission percentages. Each tier applies to the portion of revenue within that range.
                </p>

                <div className="space-y-2">
                  {commissionTiers.map((tier, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Min Revenue ($)</label>
                          <input
                            type="number"
                            min="0"
                            step="100"
                            className="input text-sm"
                            value={tier.minRevenue}
                            onChange={(e) => {
                              const newTiers = [...commissionTiers];
                              newTiers[index].minRevenue = parseFloat(e.target.value) || 0;
                              setCommissionTiers(newTiers);
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Max Revenue ($)</label>
                          <input
                            type="number"
                            min="0"
                            step="100"
                            className="input text-sm"
                            value={tier.maxRevenue ?? ''}
                            placeholder="Unlimited"
                            onChange={(e) => {
                              const newTiers = [...commissionTiers];
                              newTiers[index].maxRevenue = e.target.value ? parseFloat(e.target.value) : undefined;
                              setCommissionTiers(newTiers);
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Rate (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            className="input text-sm"
                            value={(tier.percentage * 100).toFixed(1)}
                            onChange={(e) => {
                              const newTiers = [...commissionTiers];
                              newTiers[index].percentage = (parseFloat(e.target.value) || 0) / 100;
                              setCommissionTiers(newTiers);
                            }}
                          />
                        </div>
                      </div>
                      {commissionTiers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newTiers = commissionTiers.filter((_, i) => i !== index);
                            setCommissionTiers(newTiers);
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const lastTier = commissionTiers[commissionTiers.length - 1];
                    const newMin = lastTier.maxRevenue ?? (lastTier.minRevenue + 5000);
                    setCommissionTiers([
                      ...commissionTiers.slice(0, -1),
                      { ...lastTier, maxRevenue: newMin },
                      { minRevenue: newMin, maxRevenue: undefined, percentage: lastTier.percentage + 0.01 },
                    ]);
                  }}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Plus size={16} />
                  Add Tier
                </button>

                <p className="text-xs text-gray-500">
                  Example: $0-$5,000 = 1%, $5,001-$10,000 = 2%, $10,001+ = 3%
                </p>
              </div>
            )}
          </div>

          {/* Submit buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save size={18} />
                  {isEditing ? 'Save Changes' : 'Create Company'}
                </>
              )}
            </button>
            <Link
              to={isEditing ? `/companies/${id}` : '/companies'}
              className="btn btn-secondary"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
